// MarketBand port — verbatim from immersive_gui/ui/src/components/MarketBand.jsx.
// Layout per column:
//
//   [ Spot card | ATM Vol card ]      <-- top row, two ValueCards
//   [ Forward label + value  ]        <-- combined card below
//   [ -- smile chart fills below -- ]
//
// The Forward row and the smile chart live INSIDE the lower card so they
// share a single border with consistent padding (MVP convention). No
// separate "Smile" section anywhere — the chart is part of Market Snapshot.
import { FC, useContext } from 'react';
import { Box, Stack, useTheme } from '@mui/material';

import Number from '../../number';
import { SmileSvg } from '../exotics-shared';
import { PricingContext } from '../../../../providers/exotics-pricing-provider';

import './exotics-market-band-styles.scss';

interface Props {
  instrumentId: string;
}

const ValueCard: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      // Diletta production cell chrome: translucent blue bg + cyan-grey
      // border (matches diletta-chrome.ts dilettaCellSx).
      backgroundColor: 'rgba(40, 114, 226, 0.06)',
      border: '1px solid #1f3645',
      borderRadius: '6px',
      px: 1,
      py: 0.5,
    }}
  >
    <Box sx={{ fontSize: 9, color: 'text.secondary', letterSpacing: '0.06em', mb: '2px' }}>
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: 'Inter',
        fontVariantNumeric: 'tabular-nums',
        fontSize: 13,
        fontWeight: 500,
        color: 'text.primary',
        lineHeight: 1.15,
      }}
    >
      {children}
    </Box>
  </Box>
);

const ExoticsMarketBandContent: FC<Props> = ({ instrumentId }) => {
  const { results, errors, loading, instruments } = useContext(PricingContext);
  const theme = useTheme();
  const r = results[instrumentId];
  const err = errors[instrumentId];
  // Per-token price decimals (IM-1908): resolve the instrument's base token so
  // Spot/Forward render at the market-data page's precision (MORPHO 4dp, BTC 2dp).
  const tokenSymbol = instruments.find((i) => i.id === instrumentId)?.token ?? '';
  const atmVol = r?.smile?.find((p) => p.label === 'ATMF')?.vol;
  // Section header gone — the page-level BandRow owns the label and any
  // per-band loading indicator. Keeping the loading reference satisfies
  // ESLint without bringing back the wrapper.
  void loading;

  return (
    <Stack spacing={0.75} className="exotics-market-band-content">
      {/* Spot / ATM Vol headline cards */}
      <Stack direction="row" spacing={0.75}>
        <ValueCard label="Spot">
          <Number value={r?.spot} format="price-token" symbol={tokenSymbol} role="caption" />
        </ValueCard>
        <ValueCard label="ATM Vol">
          <Number value={atmVol ?? null} format="percent" role="caption" />
        </ValueCard>
      </Stack>

      {/* Forward + Smile combined card */}
      {err ? (
        <Box sx={{ fontSize: 10, color: theme.palette.error.main, pt: 0.5 }}>{err}</Box>
      ) : !r?.smile?.length ? (
        <Box sx={{ fontSize: 10, color: 'text.disabled' }}>—</Box>
      ) : (
        <Box
          sx={{
            backgroundColor: 'rgba(40, 114, 226, 0.06)',
            border: '1px solid #1f3645',
            borderRadius: '6px',
            px: 1,
            py: 0.5,
          }}
        >
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            sx={{ mb: '2px' }}
          >
            <Box sx={{ fontSize: 9, color: 'text.secondary', letterSpacing: '0.06em' }}>
              Forward
            </Box>
            <Box
              sx={{
                fontFamily: 'Inter',
                fontVariantNumeric: 'tabular-nums',
                color: 'text.primary',
              }}
            >
              <Number value={r.forward} format="price-token" symbol={tokenSymbol} role="small" />
            </Box>
          </Stack>
          <Box sx={{ mt: '2px' }}>
            <SmileSvg smile={r.smile} height={78} />
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export default ExoticsMarketBandContent;
